// editRecordModal.shared.js — 계좌·세금정보 연결 모달 공용 헬퍼·스타일
// (EditRecordModal.jsx 542줄 분리: 셸 + 계좌 섹션 + 세금 섹션 + 본 파일)
import { todayKST } from '../../../utils/date';

export function fmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
}
export function accountLabel(row) {
  const date = row.effective_from ? ` · ${row.effective_from}부터` : '';
  return `${row.bank_name || ''} ${row.account_number || ''} (${row.account_holder || ''})${date}`;
}
export function taxLabel(row) {
  const date = row.effective_from ? ` · ${row.effective_from}부터` : '';
  if (row.tax_type_code === 'business') {
    const biz = row.business_registration_number ? ` ${row.business_registration_number}` : '';
    const bizName = row.business_name ? ` (${row.business_name})` : '';
    return `${row.name || ''} / 사업자${biz}${bizName} / 부가세 가산${date}`;
  }
  return `${row.name || ''} / ${row.resident_number_masked || '***'} / ${row.tax_rate ?? 3.3}%${date}`;
}
export function dateYmd(value) {
  return String(value || '').slice(0, 10);
}
export function getEffectiveItemId(items, settlementDateYmd) {
  if (!settlementDateYmd) return '';
  const candidates = items
    .filter((item) => {
      const ymd = dateYmd(item.effective_from);
      return item.is_active && ymd && ymd <= settlementDateYmd;
    })
    .sort((a, b) => {
      const byDate = dateYmd(b.effective_from).localeCompare(dateYmd(a.effective_from));
      if (byDate !== 0) return byDate;
      return Number(b.id || 0) - Number(a.id || 0);
    });
  return candidates[0]?.id || '';
}
export function getItemStatus(row, effectiveItemId, settlementDateYmd) {
  if (!row.is_active) {
    return { selectable: false, label: '비활성', tone: 'muted', description: '삭제되었거나 더 이상 사용하지 않는 정보입니다.' };
  }
  if (!settlementDateYmd) {
    return { selectable: true, label: '선택 가능', tone: 'ok', description: '' };
  }
  const effectiveFrom = dateYmd(row.effective_from);
  if (effectiveFrom && effectiveFrom > settlementDateYmd) {
    return { selectable: false, label: '미래 적용', tone: 'future', description: `${effectiveFrom}부터 적용되어 이 정산에는 연결할 수 없습니다.` };
  }
  if (String(row.id) === String(effectiveItemId)) {
    return { selectable: true, label: '정산일 적용', tone: 'ok', description: '이 정산일 기준으로 실제 적용되는 정보입니다.' };
  }
  return { selectable: false, label: '이전 정보', tone: 'muted', description: '정산일 기준 더 최신 정보가 있어 연결할 수 없습니다.' };
}
export function canAutoSelectNewInfo(effectiveFrom, existingItems, settlementDateYmd) {
  const ymd = dateYmd(effectiveFrom);
  if (!settlementDateYmd) return true;
  if (!ymd || ymd > settlementDateYmd) return false;
  return !existingItems.some((item) => {
    if (!item.is_active) return false;
    const itemYmd = dateYmd(item.effective_from);
    return itemYmd && itemYmd <= settlementDateYmd && itemYmd > ymd;
  });
}
export function changeModeForDate(ymd) {
  const date = String(ymd || '').slice(0, 10);
  const today = todayKST();
  if (date < today) return 'correction';
  if (date > today) return 'scheduled';
  return 'today';
}

// ── 스타일 (전부 정적) ──────────────────────────────────────────────────────
export const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};
export const boxStyle = {
  background: 'var(--bg-card)',
  borderRadius: 12,
  width: 'min(620px, calc(100vw - 24px))',
  maxWidth: 'calc(100vw - 24px)',
  maxHeight: 'min(92vh, calc(100dvh - 20px))',
  marginTop: 10,
  overflowY: 'auto',
  overflowX: 'hidden',
  boxShadow: '0 16px 44px rgba(0,0,0,0.24)',
  padding: '18px clamp(12px, 4vw, 22px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  boxSizing: 'border-box',
};
export const sectionStyle = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
  boxSizing: 'border-box',
};
export const titleStyle = { fontSize: 13, fontWeight: 800, color: 'var(--text)' };
export const hintStyle = { fontSize: 11, color: 'var(--text-muted)' };
export const inputStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  color: 'var(--text)',
  background: 'var(--bg-card)',
  outline: 'none',
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
};
export const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))', gap: 8, minWidth: 0 };
export const smallBtnStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  background: 'var(--bg-subtle)',
  color: 'var(--text)',
  whiteSpace: 'nowrap',
};
export const primaryBtnStyle = {
  border: 'none',
  borderRadius: 8,
  padding: '8px 18px',
  background: '#2563eb',
  color: '#fff',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
export const dangerBtnStyle = {
  border: 'none',
  background: 'transparent',
  color: '#dc2626',
  fontSize: 11,
  fontWeight: 800,
  cursor: 'pointer',
  padding: '3px 6px',
};
const statusTone = {
  ok: { color: '#047857', background: 'rgba(16, 185, 129, 0.12)', border: 'rgba(5, 150, 105, 0.26)' },
  future: { color: '#1d4ed8', background: 'rgba(37, 99, 235, 0.12)', border: 'rgba(37, 99, 235, 0.26)' },
  muted: { color: 'var(--text-muted)', background: 'var(--bg-subtle)', border: 'var(--border)' },
};
export const statusBadgeStyle = (tone = 'muted') => ({
  border: `1px solid ${statusTone[tone]?.border || 'var(--border)'}`,
  borderRadius: 999,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 900,
  color: statusTone[tone]?.color || 'var(--text-muted)',
  background: statusTone[tone]?.background || 'var(--bg-subtle)',
  whiteSpace: 'nowrap',
});
export const optionMetaStyle = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  flexWrap: 'wrap',
  minWidth: 0,
};
export const optionDescriptionStyle = {
  flexBasis: '100%',
  fontSize: 11,
  color: 'var(--text-muted)',
  lineHeight: 1.35,
};
export const errorStyle = { fontSize: 11, color: '#dc2626', fontWeight: 700 };
export const effectiveDateBadgeStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 800,
  color: 'var(--text)',
  background: 'var(--bg-subtle)',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
};
export const datePanelStyle = {
  display: 'grid',
  gap: 6,
};
export const autocompleteWrapStyle = { position: 'relative', minWidth: 0, maxWidth: '100%' };
export const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 10,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  maxHeight: 180,
  overflowY: 'auto',
  overflowX: 'hidden',
  boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
  boxSizing: 'border-box',
};
export const dropdownItemStyle = {
  padding: '8px 10px',
  fontSize: 12,
  cursor: 'pointer',
  color: 'var(--text)',
};
